import React, { useMemo } from 'react'
import Text from '../../../components/text/Text'
import { IPromotions } from '../../../apis/promotion/list'
import { useTime } from '../../../utils/time'
import WizButton from '../../../components/button/WizButton'

interface IProps {
    promotions: IPromotions[]
    activePromotionId?: string | null
    onSelect: (id: string | null) => void
    isCompact?: boolean
    hasNextPage?: boolean
    isFetchingNextPage?: boolean
    onLoadMore?: () => void
}

export function PromotionList({
    promotions,
    activePromotionId,
    onSelect,
    isCompact = false,
    hasNextPage = false,
    isFetchingNextPage = false,
    onLoadMore,
}: IProps) {
    const sortedPromotions = useMemo(() => {
        return promotions?.sort((a, b) => {
            const now = new Date()
            const isFutureA = new Date(a.end_at) > now
            const isFutureB = new Date(b.end_at) > now

            const priorityA = a.id === '15' || a.id === '16' ? 0 : a.priority
            const priorityB = b.id === '15' || b.id === '16' ? 0 : b.priority

            if (isFutureA && !isFutureB) return -1
            if (!isFutureA && isFutureB) return 1
            return priorityB - priorityA
        })
    }, [promotions])

    const renderStatusDot = (promotion: IPromotions) => {
        const now = new Date()
        const start = new Date(promotion.start_at)
        const end = new Date(promotion.end_at)
        let color = 'bg-gray-300'

        if (promotion.status === 'active') {
            if (now < start) {
                color = 'bg-orange-500' // Scheduled
            } else if (now > end) {
                color = 'bg-gray-400' // Ended
            } else {
                color = 'bg-green-500' // Active
            }
        }
        return <div className={`w-1.5 h-1.5 rounded-full ${color} flex-shrink-0`} />
    }

    return (
        <div className="space-y-2">
            {sortedPromotions?.map(promotion => {
                const isActive = activePromotionId === promotion.id
                return (
                    <div
                        key={promotion.id}
                        onClick={() => onSelect(promotion.id)}
                        className={`cursor-pointer transition-all ${isActive
                                ? 'bg-primary/5 border-primary shadow-sm ring-1 ring-primary'
                                : 'bg-white border-line02 hover:border-primary/50'
                            } ${isCompact ? 'p-2 rounded-md border' : 'p-3 rounded-lg border'}`}>
                        {isCompact ? (
                            // Compact View: Priority + Truncated Name
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <span
                                        className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-[10px] font-bold ${isActive ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                        {promotion.priority}
                                    </span>
                                    <span
                                        className={`px-1 rounded-[3px] text-[9px] font-bold ${promotion.language === 'en'
                                                ? 'bg-blue-100 text-blue-600'
                                                : 'bg-purple-100 text-purple-600'
                                            }`}>
                                        {promotion.language.toUpperCase()}
                                    </span>
                                </div>
                                <Text
                                    className={`text-ti3 font-medium truncate flex-1 min-w-0 ${isActive ? 'text-primary' : 'text-font01'
                                        }`}>
                                    {promotion.name}
                                </Text>
                                {renderStatusDot(promotion)}
                            </div>
                        ) : (
                            // Full View (Original)
                            <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0 mr-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Text
                                            className={`text-ti2 font-bold truncate ${isActive ? 'text-primary' : 'text-font01'
                                                }`}>
                                            {promotion.name}
                                        </Text>
                                        {renderStatusDot(promotion)}
                                    </div>
                                    <div className="flex items-center gap-2 text-font03">
                                        <Text className="text-ca2">ID: {promotion.id}</Text>
                                        <div className="w-px h-2 bg-line02" />
                                        <Text className="text-ca2">
                                            {new Date(promotion.start_at).toLocaleDateString()} -{' '}
                                            {new Date(promotion.end_at).toLocaleDateString()}
                                        </Text>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                    <div
                                        className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isActive ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                        P{promotion.priority}
                                    </div>
                                    <span
                                        className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${promotion.language === 'en'
                                                ? 'bg-blue-100 text-blue-600'
                                                : 'bg-purple-100 text-purple-600'
                                            }`}>
                                        {promotion.language.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )
            })}

            {/* admin/promotions 는 10개씩 페이징되므로 마지막 페이지 전까지 다음 페이지를 이어서 불러온다. */}
            {hasNextPage && (
                <WizButton
                    text={isFetchingNextPage ? 'Loading...' : 'Load More'}
                    type="border"
                    disabled={isFetchingNextPage}
                    onClick={onLoadMore}
                    className="w-full !mt-3"
                />
            )}
        </div>
    )
}
